import React, { useMemo } from "react";
import { humanizeNumbers } from "../../../utils/format";

type CancellationStatus = { icon: string; text: string };
type CancellationPolicy = {
  min_days_before_checkin?: number | null;
  max_days_before_checkin?: number | null;
  min_hours_till_start?: number | null;
  max_hours_till_start?: number | null;
  cancellation_charge: number;
};

const REFUND_STATUSES = {
  FULL: { icon: "✅", text: "Free cancellation, full refund" },
  NONE: { icon: "🚫", text: "No refund, full advance retained" },
  PARTIAL: (amount: string) => ({
    icon: "✂️",
    text: `₹${amount} refund`,
  }),
} as const;

const getCancellationStatus = (
  charge: number,
  totalAmount?: number,
  paidAmount?: number
): CancellationStatus => {
  if (!totalAmount || !paidAmount) {
    return charge === 0
      ? REFUND_STATUSES.FULL
      : charge === 100
      ? REFUND_STATUSES.NONE
      : { icon: "✂️", text: `${charge}% fee (on total booking amount)` };
  }

  const refundAmount = Math.min(paidAmount, totalAmount * (1 - charge / 100));

  return charge === 0
    ? REFUND_STATUSES.FULL
    : charge === 100
    ? REFUND_STATUSES.NONE
    : REFUND_STATUSES.PARTIAL(humanizeNumbers(refundAmount));
};

const getDaysRange = (
  minDays?: number | null,
  maxDays?: number | null,
  minHours?: number | null,
  maxHours?: number | null
): string => {
  const convertHoursToDays = (hours?: number | null) =>
    hours ? Math.ceil(hours / 24) : null;

  const min = minHours ? convertHoursToDays(minHours) : minDays ?? null;
  const max = maxHours ? convertHoursToDays(maxHours) : maxDays ?? null;

  return !min && !max
    ? "Any time"
    : !min
    ? `Up to ${max} days`
    : !max
    ? `${min}+ days`
    : `${min} - ${max} days`;
};

export const CancellationPolicyGrid: React.FC<{
  cancelInfo: CancellationPolicy[];
  totalAmount?: number;
  paidAmount?: number;
}> = ({ cancelInfo, totalAmount, paidAmount }) => {
  const sortedPolicies = useMemo(
    () =>
      [...cancelInfo].sort(
        (currentPolicy, nextPolicy) =>
          (currentPolicy.cancellation_charge ?? -Infinity) -
          (nextPolicy.cancellation_charge ?? -Infinity)
      ),
    [cancelInfo]
  );

  if (!cancelInfo.length) {
    return (
      <div className="text-secondary">
        No cancellation policy information available
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {sortedPolicies.map((policy, index) => {
        const { icon, text } = getCancellationStatus(
          policy.cancellation_charge,
          totalAmount,
          paidAmount
        );

        const daysRange = getDaysRange(
          policy.min_days_before_checkin,
          policy.max_days_before_checkin,
          policy.min_hours_till_start,
          policy.max_hours_till_start
        );

        return (
          <div
            key={`policy-${index}-${policy.cancellation_charge}`}
            className="flex flex-col p-4"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{icon}</span>
              <div className="flex flex-col gap-1">
                <span className="text-secondary text-base">{daysRange}</span>
                <span className="text-primary text-base">{text}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CancellationPolicyGrid;
